import React, { useState, Children, lazy } from 'react';
import { motion } from 'framer-motion';
import { Bell, FlaskConical, Microscope, ShieldCheck, ArrowRight, CheckCircle2, AlertCircle, Lock, ShoppingCart } from 'lucide-react';
import { shopData, Category, Product } from './shopData';
import { useCart } from '../../../contexts/CartContext';
import { usePrice } from '../../../contexts/PriceContext';
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xkoyvnke';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
// Stylized bottle mockup — CSS-only since real product photos don't exist yet
function BottleMockup({
  product
}: {
  product: Product;
}) {
  const colorMap = {
    charcoal: 'bg-charcoal',
    burgundy: 'bg-burgundy',
    navyslate: 'bg-navyslate',
    tealslate: 'bg-tealslate'
  };
  const accentMap = {
    tealslate: 'bg-tealslate',
    lightteal: 'bg-lightteal'
  };
  return <div className="relative w-full h-full flex items-end justify-center pb-4" data-id="element-1880">
      {/* Bottle */}
      <div className="relative w-[55%] aspect-[1/2.2] flex flex-col" data-id="element-1881">
        {/* Cap */}
        <div className={`${accentMap[product.accentColor]} h-[12%] rounded-t-md mx-2 shadow-md`} data-id="element-1882" />
        {/* Neck */}
        <div className={`${colorMap[product.bottleColor]} h-[5%] mx-3 opacity-70`} data-id="element-1883" />
        {/* Body */}
        <div className={`${colorMap[product.bottleColor]} flex-1 rounded-md shadow-2xl relative overflow-hidden border border-white/5`} data-id="element-1884">
          {/* Highlight */}
          <div className="absolute top-2 left-2 bottom-2 w-[8%] bg-white/10 rounded-full blur-sm" data-id="element-1885" />

          {/* Label */}
          <div className="absolute inset-x-3 top-[20%] bottom-[20%] bg-[#F5F5F0] flex flex-col items-center justify-between p-3 shadow-inner" data-id="element-1886">
            {/* Top: brand */}
            <div className="text-center" data-id="element-1887">
              <p className="text-[6px] md:text-[7px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em]" data-id="element-1888">
                Effective Dose
              </p>
              <div className={`w-3 h-[1px] ${accentMap[product.accentColor]} mx-auto mt-1`} data-id="element-1889" />
            </div>

            {/* Middle: product name */}
            <div className="text-center" data-id="element-1890">
              <p className="text-[8px] md:text-[10px] font-serif text-[#1F1F1F] leading-tight" data-id="element-1891">
                {product.name}
              </p>
            </div>

            {/* Bottom: dose */}
            <div className="text-center" data-id="element-1892">
              <p className="text-[14px] md:text-[18px] font-serif font-bold text-[#1F1F1F] leading-none" data-id="element-1893">
                {product.dose}
              </p>
              <p className="text-[5px] md:text-[6px] text-[#7A7A70] uppercase tracking-widest mt-0.5" data-id="element-1894">
                {product.doseUnit}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Floor shadow */}
      <div className="absolute bottom-0 left-1/4 right-1/4 h-2 bg-black/30 rounded-full blur-md" data-id="element-1895" />
    </div>;
}
interface WaitlistFormProps {
  productName?: string;
}
function WaitlistForm({
  productName
}: WaitlistFormProps) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    if (formData.get('_gotcha')) {
      setStatus('success');
      return;
    }
    setStatus('submitting');
    setError('');
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data?.error || 'Something went wrong.');
        setStatus('error');
      }
    } catch {
      setError('Network error. Try again.');
      setStatus('error');
    }
  };
  if (status === 'success') {
    return <div className="bg-tealslate/20 border-l-4 border-tealslate p-6 flex gap-4 items-start" data-id="element-1896">
        <CheckCircle2 className="w-6 h-6 text-tealslate shrink-0" data-id="element-1897" />
        <div data-id="element-1898">
          <p className="font-bold text-white mb-1" data-id="element-1899">You're on the list.</p>
          <p className="text-sm text-[#D8D8D0]" data-id="element-1900">
            We'll email you the moment Effective Dose launches.
          </p>
        </div>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="w-full" data-id="element-1901">
      <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" style={{
      position: 'absolute',
      left: '-9999px'
    }} aria-hidden="true" data-id="element-1902" />
      <input type="hidden" name="form_type" value="effective_dose_waitlist" data-id="element-1903" />
      {productName && <input type="hidden" name="product_interest" value={productName} data-id="element-1904" />}

      <div className="flex flex-col sm:flex-row gap-3" data-id="element-1905">
        <input type="email" name="email" placeholder="Enter your email" required className="flex-1 bg-bodybg border border-darkgray px-5 py-4 text-white placeholder:text-[#7A7A70] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1906" />
        <button type="submit" disabled={status === 'submitting'} className="px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-lightteal transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-1907">
          {status === 'submitting' ? <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1908" />
              Joining…
            </> : <>
              Notify Me <ArrowRight className="w-4 h-4" data-id="element-1909" />
            </>}
        </button>
      </div>

      {status === 'error' && <div className="mt-3 flex items-center gap-2 text-sm text-burgundy" data-id="element-1910">
          <AlertCircle className="w-4 h-4" data-id="element-1911" />
          {error}
        </div>}
    </form>;
}
export function Shop() {
  const [activeCategory, setActiveCategory] = useState<Category | 'All'>('All');
  const {
    addItem
  } = useCart();
  const categories: (Category | 'All')[] = ['All', 'Performance', 'Foundation', 'Recovery'];
  const filteredProducts = activeCategory === 'All' ? shopData : shopData.filter(p => p.category === activeCategory);
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-1912">
      {/* HERO */}
      <section className="bg-bodybg pt-32 pb-20 md:pb-28 px-6 border-b border-darkgray relative overflow-hidden" data-id="element-1913">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-tealslate/5 rounded-full blur-3xl" data-id="element-1914" />

        <div className="max-w-5xl mx-auto text-center relative z-10" data-id="element-1915">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="flex flex-col items-center" data-id="element-1916">
            <motion.div variants={fadeUpVariants} className="inline-flex items-center gap-2 px-4 py-2 border border-tealslate/40 mb-8" data-id="element-1917">
              <span className="w-1.5 h-1.5 bg-tealslate rounded-full" data-id="element-1918" />
              <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em]" data-id="element-1919">
                The Supplement Line
              </p>
            </motion.div>

            <motion.h1 variants={fadeUpVariants} className="text-5xl md:text-7xl lg:text-8xl font-serif text-white leading-[1] mb-8 tracking-tight" data-id="element-1920">
              Effective Dose.
            </motion.h1>

            <motion.div variants={fadeUpVariants} className="w-16 h-0.5 bg-tealslate mb-10" data-id="element-1921" />

            <motion.p variants={fadeUpVariants} className="text-xl md:text-2xl font-serif italic text-tealslate max-w-2xl leading-relaxed mb-8" data-id="element-1922">
              "If your supplement isn't dosed at what the research demands, it
              isn't working."
            </motion.p>

            <motion.p variants={fadeUpVariants} className="text-base md:text-lg text-[#D8D8D0] max-w-2xl mb-10 leading-relaxed" data-id="element-1923">
              A supplement line built on a single principle: deliver the
              clinically effective dose. No proprietary blends. No fairy dust.
              No marketing math. Just the amount your body actually needs to do
              what the science promises.
            </motion.p>

            <motion.div variants={fadeUpVariants} className="w-full max-w-lg" data-id="element-1924">
              <WaitlistForm data-id="element-1925" />
              <p className="text-xs text-[#7A7A70] mt-4" data-id="element-1926">
                Be the first to know when Effective Dose launches.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* THE PHILOSOPHY */}
      <section className="bg-[#F5F5F0] py-20 md:py-28 px-6" data-id="element-1927">
        <div className="max-w-7xl mx-auto" data-id="element-1928">
          <div className="text-center mb-16" data-id="element-1929">
            <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-1930">
              The Effective Dose Philosophy
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-[#1F1F1F] max-w-3xl mx-auto leading-tight" data-id="element-1931">
              The supplement industry has a dosing problem. We don't.
            </h2>
            <div className="w-12 h-0.5 bg-tealslate mx-auto mt-6" data-id="element-1932" />
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-1933">
            {[{
            icon: FlaskConical,
            problem: 'The Industry',
            title: 'Proprietary blends hide underdosing.',
            solution: 'Every Effective Dose product transparently lists the exact milligrams of every active ingredient. No "matrix." No "complex." Just the truth.'
          }, {
            icon: Microscope,
            problem: 'The Industry',
            title: 'Cheap, poorly absorbed forms.',
            solution: 'We use methylated B-vitamins, chelated minerals, KSM-66 ashwagandha — the bioavailable forms shown in clinical research, not the ones with the best margins.'
          }, {
            icon: ShieldCheck,
            problem: 'The Industry',
            title: 'Marketing claims without backing.',
            solution: 'Every dose in our line is tied to peer-reviewed research. If the studies say 5g of creatine, you get 5g. If they say 600mg of KSM-66, you get 600mg.'
          }].map((item, i) => <motion.div key={i} variants={fadeUpVariants} className="bg-white border border-[#D8D8D0] p-8 flex flex-col h-full" data-id="element-1934">
                <item.icon className="w-7 h-7 text-tealslate mb-6" data-id="element-1935" />
                <p className="text-[10px] font-bold text-burgundy uppercase tracking-widest mb-3" data-id="element-1936">
                  {item.problem}
                </p>
                <h3 className="text-xl font-serif text-[#1F1F1F] mb-4 leading-tight" data-id="element-1937">
                  {item.title}
                </h3>
                <p className="text-sm text-[#3B3B3B] leading-relaxed" data-id="element-1938">
                  <span className="font-bold text-tealslate" data-id="element-1939">
                    Our standard:{' '}
                  </span>
                  {item.solution}
                </p>
              </motion.div>)}
          </motion.div>

          {/* Manifesto pull quote */}
          <div className="border-l-4 border-tealslate pl-8 py-6 max-w-3xl mx-auto" data-id="element-1940">
            <p className="text-2xl md:text-3xl font-serif italic text-[#1F1F1F] leading-relaxed" data-id="element-1941">
              "If you're going to take a supplement, take a dose that actually
              does something."
            </p>
            <p className="text-xs text-tealslate uppercase tracking-widest mt-4 font-bold" data-id="element-1942">
              — Jack Achenbach, MS, RDN, CPT
            </p>
          </div>
        </div>
      </section>

      {/* CATEGORY FILTER */}
      <div className="sticky top-[88px] z-40 bg-charcoal border-b border-darkgray shadow-md" data-id="element-1943">
        <div className="max-w-7xl mx-auto px-6 py-4 overflow-x-auto" data-id="element-1944">
          <div className="flex items-center gap-3 min-w-max" data-id="element-1945">
            {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${activeCategory === cat ? 'bg-tealslate text-white' : 'bg-transparent text-[#9CA3AF] border border-darkgray hover:text-white hover:border-[#9CA3AF]'}`} data-id="element-1946">
                {cat}
              </button>)}
          </div>
        </div>
      </div>

      {/* PRODUCT GRID */}
      <section className="bg-bodybg py-20 px-6" data-id="element-1947">
        <div className="max-w-7xl mx-auto" data-id="element-1948">
          <div className="mb-12" data-id="element-1949">
            <p className="text-[10px] font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-4" data-id="element-1950">
              The Launch Lineup
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-1951">
              Nine essentials. Each at its effective dose.
            </h2>
          </div>

          <motion.div key={activeCategory} initial="hidden" animate="visible" variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1952">
            {filteredProducts.map(product => <motion.div key={product.id} variants={fadeUpVariants} className="bg-navyslate/30 border border-darkgray flex flex-col h-full hover:border-tealslate/60 transition-colors group" data-id="element-1953">
                {/* Bottle area */}
                <div className="relative aspect-square bg-[#F5F5F0] border-b border-darkgray overflow-hidden" data-id="element-1954">
                  <span className="absolute top-4 left-4 text-[10px] font-bold text-tealslate uppercase tracking-widest z-10 px-2 py-1 bg-white/90 backdrop-blur-sm" data-id="element-1955">
                    {product.category}
                  </span>
                  <img src={product.image} alt={product.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1956" />
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1" data-id="element-1957">
                  <h3 className="text-2xl font-serif text-white mb-2 leading-tight" data-id="element-1958">
                    {product.name}
                  </h3>
                  <p className="text-sm text-tealslate font-serif italic mb-4" data-id="element-1959">
                    {product.tagline}
                  </p>

                  {/* Dose callout */}
                  <div className="bg-bodybg border-l-2 border-tealslate px-4 py-3 mb-4" data-id="element-1960">
                    <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-widest mb-1" data-id="element-1961">
                      Effective Dose
                    </p>
                    <p className="text-2xl font-serif text-white leading-none" data-id="element-1962">
                      {product.dose}
                      <span className="text-sm text-[#9CA3AF] font-sans ml-2" data-id="element-1963">
                        {product.doseUnit}
                      </span>
                    </p>
                  </div>

                  <p className="text-sm text-[#D8D8D0] leading-relaxed mb-4" data-id="element-1964">
                    {product.shortDescription}
                  </p>

                  <details className="text-sm group/details" data-id="element-1965">
                    <summary className="cursor-pointer list-none flex items-center justify-between gap-2 py-3 border-t border-darkgray text-tealslate hover:text-lightteal transition-colors" data-id="element-1966">
                      <span className="text-xs font-bold uppercase tracking-wider" data-id="element-1967">
                        Why this dose?
                      </span>
                      <span className="group-open/details:rotate-45 transition-transform" data-id="element-1968">
                        +
                      </span>
                    </summary>
                    <p className="text-sm text-[#9CA3AF] leading-relaxed pt-3" data-id="element-1969">
                      {product.whyEffective}
                    </p>
                  </details>

                  <ProductPricing product={product} addItem={addItem} data-id="element-1970" />
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* WAITLIST FOOTER */}
      <section className="bg-burgundy py-20 md:py-28 px-6 text-center border-t border-darkgray" data-id="element-1971">
        <div className="max-w-3xl mx-auto" data-id="element-1972">
          <Bell className="w-10 h-10 text-tealslate mx-auto mb-6" data-id="element-1973" />
          <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-1974">
            Be First In Line
          </p>
          <h2 className="text-4xl md:text-5xl font-serif text-white leading-tight mb-6" data-id="element-1975">
            Effective Dose launches soon.
          </h2>
          <p className="text-lg text-[#D8D8D0] leading-relaxed mb-10 max-w-xl mx-auto" data-id="element-1976">
            Join the waitlist and you'll be the first to know when each product
            drops — plus founding-member pricing on launch day.
          </p>

          <div className="max-w-md mx-auto" data-id="element-1977">
            <WaitlistForm data-id="element-1978" />
          </div>

          <p className="text-xs text-[#9CA3AF] italic mt-8" data-id="element-1979">
            No spam. Unsubscribe anytime. Just launch updates.
          </p>
        </div>
      </section>
    </div>;
}
interface ProductPricingProps {
  product: Product;
  addItem: ReturnType<typeof useCart>['addItem'];
}
function ProductPricing({
  product,
  addItem
}: ProductPricingProps) {
  const {
    cents,
    isLive,
    loading
  } = usePrice(product.stripePriceId, product.price);
  const canBuy = cents !== null && Boolean(product.stripePriceId);
  const handleAdd = () => {
    if (cents === null) return;
    addItem({
      productId: product.id,
      name: product.name,
      unitPrice: cents,
      type: 'supplement'
    });
  };
  return <div className="flex items-center justify-between gap-4 pt-5 mt-auto border-t border-darkgray" data-id="element-1980">
      <div data-id="element-1981">
        <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-widest mb-0.5" data-id="element-1982">
          Price
        </p>
        {cents === null ? <div className="h-7 w-20 bg-darkgray/60 animate-pulse rounded-sm" data-id="element-1983" /> : <p className="text-2xl font-serif text-white leading-none" data-id="element-1984">
            ${(cents / 100).toFixed(2)}
            {!isLive && !loading && <span className="text-[10px] text-[#7A7A70] font-sans ml-2 normal-case tracking-normal" data-id="element-1985">
                · est.
              </span>}
          </p>}
      </div>
      <button onClick={handleAdd} disabled={!canBuy} className="px-5 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-lightteal transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2" title={!canBuy ? 'Not yet available for purchase' : undefined} data-id="element-1986">
        <ShoppingCart className="w-3.5 h-3.5" data-id="element-1987" />
        Add to Cart
      </button>
    </div>;
}